// src/pages/earningsImport/runningExpensesParser.js
import * as XLSX from "xlsx";

export function normalizeHeader(value) {
  return String(value || "")
    .replace(/\n/g, " ")
    .replace(/\r/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

export function normalizePlateDigits(value) {
  return String(value || "").replace(/\D+/g, "");
}

function toISODateFromDateObj(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

export function normalizeDate(raw) {
  if (!raw) return "";

  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return toISODateFromDateObj(raw);
  }

  if (typeof raw === "number") {
    const parsed = XLSX.SSF.parse_date_code(raw);
    if (parsed) {
      return `${parsed.y}-${String(parsed.m).padStart(2, "0")}-${String(parsed.d).padStart(
        2,
        "0"
      )}`;
    }
  }

  const text = String(raw).trim();
  if (!text) return "";

  // Salik common format: 30 Jun 2026
  const dmyText = text.match(/^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})$/);
  if (dmyText) {
    const day = dmyText[1].padStart(2, "0");
    const monthName = dmyText[2].slice(0, 3).toLowerCase();
    const year = dmyText[3];
    const months = {
      jan: "01",
      feb: "02",
      mar: "03",
      apr: "04",
      may: "05",
      jun: "06",
      jul: "07",
      aug: "08",
      sep: "09",
      oct: "10",
      nov: "11",
      dec: "12",
    };
    if (months[monthName]) return `${year}-${months[monthName]}-${day}`;
  }

  // 30-06-2026 or 30/06/2026
  const dmy = text.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`;
  }

  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) return toISODateFromDateObj(parsed);

  return "";
}

function toNumber(value) {
  if (typeof value === "number") return value;
  const cleaned = String(value ?? "")
    .replace(/,/g, "")
    .replace(/AED/gi, "")
    .trim();
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function findHeaderIndex(rows) {
  return rows.findIndex((row) => {
    const normalized = (row || []).map(normalizeHeader);
    return (
      normalized.some((x) => x === "trip date") &&
      normalized.some((x) => x === "plate") &&
      normalized.some((x) => x.includes("total amount") && x.includes("vat"))
    );
  });
}

function findColumn(headers, matcher) {
  return headers.findIndex((h) => matcher(normalizeHeader(h)));
}



function findStandardHeaderIndex(rows) {
  return rows.findIndex((row) => {
    const normalized = (row || []).map(normalizeHeader);
    const hasDate = normalized.some((h) =>
      h === "date" ||
      h === "transaction date" ||
      h === "trip date" ||
      h === "expense date" ||
      h === "posting date"
    );
    const hasPlate = normalized.some((h) =>
      h === "car number" ||
      h === "plate" ||
      h === "plate number" ||
      h === "vehicle number" ||
      h === "vehicle" ||
      h === "car"
    );
    const hasAmount = normalized.some((h) =>
      h === "amount" ||
      h === "total amount" ||
      h === "total" ||
      h === "amount aed" ||
      h === "amount (aed)" ||
      h === "expense amount" ||
      h === "fine amount" ||
      h === "fine amount aed" ||
      h === "fine amount (aed)" ||
      h === "fine aed" ||
      h === "fine"
    );
    return hasDate && hasPlate && hasAmount;
  });
}

function parseStandardRows({ rows, headerIndex, category }) {
  const headers = rows[headerIndex] || [];

  const dateCol = findColumn(headers, (h) =>
    h === "date" ||
    h === "transaction date" ||
    h === "trip date" ||
    h === "expense date" ||
    h === "posting date"
  );
  const plateCol = findColumn(headers, (h) =>
    h === "car number" ||
    h === "plate" ||
    h === "plate number" ||
    h === "vehicle number" ||
    h === "vehicle" ||
    h === "car"
  );
  const amountCol = findColumn(headers, (h) =>
    h === "amount" ||
    h === "total amount" ||
    h === "total" ||
    h === "amount aed" ||
    h === "amount (aed)" ||
    h === "expense amount" ||
    h === "fine amount" ||
    h === "fine amount aed" ||
    h === "fine amount (aed)" ||
    h === "fine aed" ||
    h === "fine"
  );
  const refCol = findColumn(headers, (h) =>
    h === "reference" || h === "reference no" || h === "reference number" || h === "transaction id" || h === "voucher no"
  );
  const remarksCol = findColumn(headers, (h) =>
    h === "remarks" || h === "remark" || h === "description" || h === "details" || h === "note"
  );

  if (dateCol < 0 || plateCol < 0 || amountCol < 0) {
    throw new Error(
      "Standard expense file is missing required columns: Date, Car Number/Plate, and Amount/Fine Amount."
    );
  }

  const parsedRows = [];

  for (let r = headerIndex + 1; r < rows.length; r += 1) {
    const row = rows[r] || [];
    const date = normalizeDate(row[dateCol]);
    const plateRaw = row[plateCol];
    const plateDigits = normalizePlateDigits(plateRaw);
    const amount = toNumber(row[amountCol]);

    if (!date || !plateDigits || !amount) continue;

    const reference = String(row[refCol] || "").replace(/\.0$/, "").trim();
    const remarks = String(row[remarksCol] || "").trim();

    parsedRows.push({
      sourceRowNumber: r + 1,
      category,
      date,
      tripDate: date,
      reference,
      remarks,
      plate: String(plateRaw || "").replace(/\.0$/, "").trim(),
      plateDigits,
      amount,
      raw: {
        date: row[dateCol] || "",
        plate: row[plateCol] || "",
        amount: row[amountCol] || "",
        reference: row[refCol] || "",
        remarks: row[remarksCol] || "",
      },
    });
  }

  return parsedRows;
}

export async function parseStandardRunningExpensesFile(file, category) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, {
    type: "array",
    cellDates: true,
    raw: false,
  });

  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: "",
    blankrows: false,
    raw: false,
  });

  const headerIndex = findStandardHeaderIndex(rows);
  if (headerIndex === -1) {
    throw new Error(
      "Could not find standard header row. Expected columns: Date, Car Number or Plate, Amount."
    );
  }

  const parsedRows = parseStandardRows({ rows, headerIndex, category });

  return {
    category,
    headerRowNumber: headerIndex + 1,
    totalRowsInSheet: rows.length,
    rows: parsedRows,
  };
}

export async function parseSalikTripsFile(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, {
    type: "array",
    cellDates: true,
    raw: false,
  });

  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: "",
    blankrows: false,
    raw: false,
  });

  const headerIndex = findHeaderIndex(rows);
  if (headerIndex === -1) {
    throw new Error(
      "Could not find Salik header row. Expected columns: Trip Date, Plate, Total Amount incl. VAT."
    );
  }

  const headers = rows[headerIndex] || [];
  const transactionIdCol = findColumn(headers, (h) => h === "transaction id");
  const tripDateCol = findColumn(headers, (h) => h === "trip date");
  const tripTimeCol = findColumn(headers, (h) => h === "trip time");
  const postDateCol = findColumn(headers, (h) => h === "transaction post date");
  const tollGateCol = findColumn(headers, (h) => h === "toll gate");
  const directionCol = findColumn(headers, (h) => h === "direction");
  const tagCol = findColumn(headers, (h) => h === "tag number");
  const plateCol = findColumn(headers, (h) => h === "plate");
  const amountCol = findColumn(
    headers,
    (h) => h.includes("total amount") && (h.includes("incl") || h.includes("vat"))
  );

  if (tripDateCol < 0 || plateCol < 0 || amountCol < 0) {
    throw new Error(
      "Salik file is missing required columns: Trip Date, Plate, or Total Amount incl. VAT."
    );
  }

  const parsedRows = [];

  for (let r = headerIndex + 1; r < rows.length; r += 1) {
    const row = rows[r] || [];
    const tripDate = normalizeDate(row[tripDateCol]);
    const plateRaw = row[plateCol];
    const plateDigits = normalizePlateDigits(plateRaw);
    const amount = toNumber(row[amountCol]);

    if (!tripDate || !plateDigits || !amount) continue;

    parsedRows.push({
      sourceRowNumber: r + 1,
      transactionId: String(row[transactionIdCol] || "").trim(),
      tripDate,
      tripTime: String(row[tripTimeCol] || "").trim(),
      transactionPostDate: normalizeDate(row[postDateCol]),
      tollGate: String(row[tollGateCol] || "").trim(),
      direction: String(row[directionCol] || "").trim(),
      tagNumber: String(row[tagCol] || "").replace(/\.0$/, "").trim(),
      plate: String(plateRaw || "").replace(/\.0$/, "").trim(),
      plateDigits,
      amount,
      raw: {
        transactionId: row[transactionIdCol] || "",
        tripDate: row[tripDateCol] || "",
        tripTime: row[tripTimeCol] || "",
        transactionPostDate: row[postDateCol] || "",
        tollGate: row[tollGateCol] || "",
        direction: row[directionCol] || "",
        tagNumber: row[tagCol] || "",
        plate: row[plateCol] || "",
        totalAmountInclVat: row[amountCol] || "",
      },
    });
  }

  return {
    category: "SALIK",
    headerRowNumber: headerIndex + 1,
    totalRowsInSheet: rows.length,
    rows: parsedRows,
  };
}
